import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, TrendingUp, TrendingDown, Target, AlertTriangle,
  ArrowRight, PieChart, BarChart3, Sparkles, ChevronRight
} from 'lucide-react';
import { getCFODashboard, getStrategicAdvice } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

function formatCurrency(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value?.toFixed(0) || 0}`;
}

function KPICard({ label, value, subvalue, trend, color, icon: Icon }) {
  return (
    <div className="card-premium p-6">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-sm ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {trend >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>
      <p className="text-sm font-medium text-slate-500 mb-1">{label}</p>
      <p className="text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
      {subvalue && <p className="text-sm text-slate-500 mt-1">{subvalue}</p>}
    </div>
  );
}

export default function CFODashboardPage() {
  const [data, setData] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAdvice, setLoadingAdvice] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await getCFODashboard();
        setData(res.data);
      } catch (error) {
        console.error('Failed to fetch CFO dashboard:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleGetAdvice = async () => {
    setLoadingAdvice(true);
    try {
      const res = await getStrategicAdvice();
      setAdvice(res.data.advice);
    } catch (error) {
      console.error('Failed to get advice:', error);
    } finally {
      setLoadingAdvice(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6" data-testid="cfo-dashboard-loading">
        <div className="skeleton h-8 w-48 mb-6" />
        <div className="grid grid-cols-4 gap-6 mb-6">
          {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-40 rounded-xl" />)}
        </div>
      </div>
    );
  }

  const savings = data?.savings || {};
  const ytdVariance = (savings.ytd?.actual || 0) - (savings.ytd?.projected || 0);
  const variancePercent = savings.ytd?.projected ? (ytdVariance / savings.ytd.projected * 100) : 0;

  return (
    <div className="p-6" data-testid="cfo-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">CFO Dashboard</h1>
          <p className="text-slate-500">Financial control center for medication procurement</p>
        </div>
        <button 
          onClick={handleGetAdvice}
          disabled={loadingAdvice}
          className="btn-primary flex items-center gap-2"
          data-testid="get-advice-btn"
        >
          <Sparkles className="w-4 h-4" />
          {loadingAdvice ? 'Analyzing...' : 'Get Strategic Advice'}
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <KPICard
          icon={DollarSign}
          label="Savings This Month"
          value={formatCurrency(savings.this_month?.actual || 0)}
          subvalue={`Projected: ${formatCurrency(savings.this_month?.projected || 0)}`}
          color="bg-emerald-500"
        />
        <KPICard
          icon={TrendingUp}
          label="YTD Savings"
          value={formatCurrency(savings.ytd?.actual || 0)}
          trend={variancePercent}
          color="bg-[#0A2540]"
        />
        <KPICard
          icon={Target}
          label="Pipeline Value"
          value={formatCurrency(data?.pipeline_value || 0)}
          subvalue="Approved, pending execution"
          color="bg-blue-500"
        />
        <KPICard
          icon={AlertTriangle}
          label="Missed Opportunities"
          value={formatCurrency(data?.missed_opportunities?.value || 0)}
          subvalue={`${data?.missed_opportunities?.count || 0} molecules not acted on`}
          color="bg-amber-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Savings Trend Chart */}
        <div className="lg:col-span-2 card-premium p-6" data-testid="savings-trend-chart">
          <h2 className="font-semibold text-slate-900 mb-4">Savings Trend (6 Months)</h2>
          <div className="h-72">
            {savings.monthly_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={savings.monthly_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="_id" tick={{ fontSize: 12, fill: '#64748B' }} />
                  <YAxis tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 12, fill: '#64748B' }} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Bar dataKey="projected" name="Projected" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="actual" name="Actual" fill="#059669" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400">
                No trend data available
              </div>
            )}
          </div>
        </div>

        {/* Top Molecules */}
        <div className="card-premium p-6" data-testid="top-molecules">
          <h2 className="font-semibold text-slate-900 mb-4">Top Savings Drivers</h2>
          <div className="space-y-4">
            {(savings.top_molecules || []).map((mol, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-sm font-bold text-emerald-600">
                    {idx + 1}
                  </div>
                  <span className="font-medium text-slate-900">{mol.drug_name || mol._id}</span>
                </div>
                <span className="text-emerald-600 font-semibold">
                  {formatCurrency(mol.total_savings)}
                </span>
              </div>
            ))}
            {(!savings.top_molecules || savings.top_molecules.length === 0) && (
              <p className="text-sm text-slate-500 text-center py-4">No savings data yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Pipeline Status */}
      <div className="card-premium p-6 mb-6" data-testid="pipeline-status">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-slate-900">Pipeline Status</h2>
          <Link to="/pipeline" className="text-sm text-[#0A2540] hover:underline flex items-center gap-1">
            View Pipeline <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-5 gap-4">
          {['opportunity', 'under_review', 'approved', 'executed', 'impact_realized'].map((stage, idx) => (
            <div key={stage} className="text-center p-4 bg-slate-50 rounded-xl">
              <p className="text-2xl font-bold text-slate-900">{data?.pipeline?.counts?.[stage] || 0}</p>
              <p className="text-xs text-slate-500 capitalize mt-1">{stage.replace('_', ' ')}</p>
              <p className="text-xs text-emerald-600 mt-2">
                {formatCurrency(data?.pipeline?.values?.[stage] || 0)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* AI Strategic Advice */}
      {advice && (
        <div className="card-premium p-6 bg-gradient-to-br from-[#0A2540] to-[#1e3a5f] text-white" data-testid="ai-advice">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <h2 className="font-semibold">Strategic Recommendations</h2>
          </div>
          <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap">
            {advice}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <Link to="/savings" className="card-premium p-4 flex items-center justify-between group">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-slate-400" />
            <span className="font-medium text-slate-900">Savings Ledger</span>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600" />
        </Link>
        <Link to="/action-center" className="card-premium p-4 flex items-center justify-between group">
          <div className="flex items-center gap-3">
            <Target className="w-5 h-5 text-slate-400" />
            <span className="font-medium text-slate-900">Action Center</span>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600" />
        </Link>
        <Link to="/formulary" className="card-premium p-4 flex items-center justify-between group">
          <div className="flex items-center gap-3">
            <PieChart className="w-5 h-5 text-slate-400" />
            <span className="font-medium text-slate-900">Formulary View</span>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600" />
        </Link>
      </div>
    </div>
  );
}
